import { useEffect, useRef, useState, useCallback } from "react";
import type { SupabaseClient, RealtimeChannel } from "@supabase/supabase-js";
import {
  getActivityStatus,
  type ActivityStatus,
} from "../utils/activity-status";

/**
 * Presence 상태를 위한 메타데이터
 */
interface PresencePayload {
  user_id: string;
  status: "online" | "away";
  joined_at: string;
}

/**
 * usePresenceStatus 훅 옵션
 */
export interface UsePresenceStatusOptions {
  /** 채팅방 ID (presence 채널 식별용) */
  roomId: string;
  /** 현재 사용자 ID (자신의 presence 추적용) */
  currentUserId: string;
  /** 상대방 사용자 ID (상대방 상태 감지용) */
  otherUserId: string;
  /** Supabase 클라이언트 인스턴스 */
  supabaseClient: SupabaseClient;
  /** 초기 lastLoginAt (fallback용) */
  initialLastLoginAt?: string;
}

/**
 * Supabase Presence 기반 실시간 활동 상태 훅
 *
 * **업계 표준 패턴 (Slack/Discord 방식)**:
 * - 채팅방 입장 시 자동으로 online 상태 추적
 * - 브라우저 종료/네트워크 끊김 시 자동 offline 감지
 * - 상대방 입장/이탈 실시간 감지
 *
 * **기능:**
 * 1. Presence 채널 구독 및 자신의 상태 추적 (track)
 * 2. 상대방 join/leave 이벤트 실시간 감지
 * 3. 상대방 offline 시 마지막 활동 시간 표시
 *
 * @example
 * ```tsx
 * const { isActive, statusText } = usePresenceStatus({
 *   roomId: 'room-123',
 *   currentUserId: 'user-456',
 *   otherUserId: 'user-789',
 *   supabaseClient: supabase,
 * });
 *
 * return (
 *   <span className={isActive ? 'text-green-600' : 'text-slate-500'}>
 *     {statusText}
 *   </span>
 * );
 * ```
 */
export function usePresenceStatus({
  roomId,
  currentUserId,
  otherUserId,
  supabaseClient,
  initialLastLoginAt,
}: UsePresenceStatusOptions): ActivityStatus {
  const [isOnline, setIsOnline] = useState(false);
  const [lastSeenAt, setLastSeenAt] = useState<string | null>(
    initialLastLoginAt ?? null,
  );
  const channelRef = useRef<RealtimeChannel | null>(null);

  /**
   * Presence 상태에서 상대방 온라인 여부 확인
   */
  const checkOtherUserPresence = useCallback(
    (channel: RealtimeChannel) => {
      const state = channel.presenceState<PresencePayload>();

      // presenceState는 { [key: string]: PresencePayload[] } 형태
      // key는 presence config에서 설정한 값 (여기서는 currentUserId)
      let foundOtherUser = false;
      let otherUserJoinedAt: string | null = null;

      Object.entries(state).forEach(([_key, presences]) => {
        presences.forEach((presence) => {
          if (presence.user_id === otherUserId) {
            foundOtherUser = true;
            otherUserJoinedAt = presence.joined_at;
          }
        });
      });

      setIsOnline(foundOtherUser);
      if (otherUserJoinedAt) {
        setLastSeenAt(otherUserJoinedAt);
      }
    },
    [otherUserId],
  );

  useEffect(() => {
    // 필수 파라미터 검증
    if (!roomId || !currentUserId || !otherUserId) {
      return;
    }

    const channelName = `presence:room:${roomId}`;

    // Presence 채널 생성
    const channel = supabaseClient.channel(channelName, {
      config: {
        presence: {
          key: currentUserId, // 자신의 presence key
        },
      },
    });

    // 1. sync 이벤트: 초기 상태 및 상태 변경 시
    channel.on("presence", { event: "sync" }, () => {
      checkOtherUserPresence(channel);
    });

    // 2. join 이벤트: 새 사용자 입장
    channel.on("presence", { event: "join" }, ({ newPresences }) => {
      // Supabase Presence 타입은 { presence_ref: string, ...payload } 형태
      const otherUserJoined = newPresences?.some(
        (p) => (p as unknown as PresencePayload).user_id === otherUserId,
      );
      if (otherUserJoined) {
        setIsOnline(true);
        setLastSeenAt(new Date().toISOString());
      }
    });

    // 3. leave 이벤트: 사용자 이탈
    channel.on("presence", { event: "leave" }, ({ leftPresences }) => {
      // Supabase Presence 타입은 { presence_ref: string, ...payload } 형태
      const otherUserLeft = leftPresences?.some(
        (p) => (p as unknown as PresencePayload).user_id === otherUserId,
      );
      if (otherUserLeft) {
        setIsOnline(false);
        setLastSeenAt(new Date().toISOString());
      }
    });

    // 채널 구독 및 자신의 presence 추적
    channel.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        // 자신의 presence 상태 추적
        await channel.track({
          user_id: currentUserId,
          status: "online",
          joined_at: new Date().toISOString(),
        } satisfies PresencePayload);
      }
    });

    channelRef.current = channel;

    // 클린업: 채널 구독 해제 (자동으로 leave 이벤트 발생)
    return () => {
      void channel.unsubscribe();
      channelRef.current = null;
    };
  }, [
    roomId,
    currentUserId,
    otherUserId,
    supabaseClient,
    checkOtherUserPresence,
  ]);

  // 상태 텍스트 계산
  const statusText = isOnline
    ? "현재 활동 중"
    : getActivityStatus(lastSeenAt).statusText;

  return {
    isActive: isOnline,
    statusText,
  };
}
